import { useMemo, useRef, useState } from 'react';
import { Button, Icon, ActionsMenu, ActionsMenuItem, showToast } from '@repo/ui';
import { ExportType } from '@repo/types';
import { ArCollectionQueueFilter, ArCollectionQueueParams } from '../../types';
import { useCollectionQueueExport, useCollectionQueuePdfExport } from '../../queries';

type CollectionExportButtonProps = {
  queryParams: ArCollectionQueueParams;
  exportFilename: string;
  isDataLoading?: boolean;
  exportMeta?: { title?: string; headers?: string[][] };
};

export default function CollectionExportButton({
  queryParams,
  exportFilename,
  isDataLoading,
  exportMeta,
}: CollectionExportButtonProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const exportBtnRef = useRef<HTMLButtonElement>(null);

  // "Export Table" — exports current table view
  const tableExportParams = useMemo(
    () => ({
      ...queryParams,
      filters: {
        ...(queryParams.filters as ArCollectionQueueFilter),
        exportType: ExportType.TABLE,
        exportFilename: `${exportFilename}-table`,
        exportMeta,
      },
    }),
    [queryParams, exportFilename, exportMeta]
  );
  const { isFetching: isExportingTable, refetch: exportTable } =
    useCollectionQueueExport(tableExportParams as ArCollectionQueueParams);

  // "Export by Matters" — detailed invoice breakdown
  const mattersExportParams = useMemo(
    () => ({
      ...queryParams,
      limit: undefined,
      page: undefined,
      filters: {
        ...(queryParams.filters as ArCollectionQueueFilter),
        exportType: ExportType.INVOICE_DETAILS,
        exportFilename: 'Outstanding-AR-Invoices-Report',
        exportMeta,
      },
    }),
    [queryParams, exportFilename, exportMeta]
  );
  const { isFetching: isExportingMatters, refetch: exportByMatters } =
    useCollectionQueueExport(mattersExportParams as ArCollectionQueueParams);

  // "Export Table as PDF" params
  const tablePdfParams = useMemo(
    () => ({
      ...queryParams,
      filters: {
        ...(queryParams.filters as ArCollectionQueueFilter),
        exportType: (ExportType.TABLE_PDF ?? 'table_pdf') as ExportType,
        exportMeta,
      },
    }),
    [queryParams, exportMeta]
  );

  // "Export Invoice Details as PDF" params
  const invoiceDetailPdfParams = useMemo(
    () => ({
      ...queryParams,
      limit: undefined,
      page: undefined,
      filters: {
        ...(queryParams.filters as ArCollectionQueueFilter),
        exportType: (ExportType.INVOICE_DETAILS_PDF ?? 'invoice_details_pdf') as ExportType,
        exportMeta,
      },
    }),
    [queryParams, exportMeta]
  );

  const { mutateAsync: exportPdf, isPending: isExportingPdf } = useCollectionQueuePdfExport();

  const handlePdfExport = async (params: ArCollectionQueueParams) => {
    try {
      await exportPdf(params);
    } catch (err: any) {
      if (err?.message === 'POPUP_BLOCKED') {
        showToast.warning('Pop-up blocked. Please allow pop-ups for this site and try again.');
      } else {
        showToast.error(err?.message || 'Failed to generate PDF. Please try again.');
      }
    }
  };

  const isExporting = isExportingTable || isExportingMatters || isExportingPdf;

  return (
    <div className="relative">
      <Button
        ref={exportBtnRef}
        variant="outline"
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        disabled={isDataLoading || isExporting}
        loading={isExporting}
        leftIcon={<Icon name="HiDownload" className="h-4 w-4" />}
        rightIcon={<Icon name="HiChevronDown" className="h-4 w-4" />}
      >
        Export
      </Button>
      <ActionsMenu
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
        triggerElement={exportBtnRef.current}
      >
        <ActionsMenuItem
          icon="HiTable"
          label="Export Table"
          onClick={() => {
            setIsMenuOpen(false);
            exportTable();
          }}
          disabled={isDataLoading || isExporting}
        />
        <ActionsMenuItem
          icon="HiDocumentText"
          label="Export Invoice Details"
          onClick={() => {
            setIsMenuOpen(false);
            exportByMatters();
          }}
          disabled={isDataLoading || isExporting}
        />
        <ActionsMenuItem
          icon="HiTable"
          iconColor="text-red-600"
          label="Export Table as PDF"
          onClick={() => {
            setIsMenuOpen(false);
            handlePdfExport(tablePdfParams as ArCollectionQueueParams);
          }}
          disabled={isDataLoading || isExporting}
        />
        <ActionsMenuItem
          icon="HiDocumentText"
          iconColor="text-red-600"
          label="Export Invoice Details as PDF"
          onClick={() => {
            setIsMenuOpen(false);
            handlePdfExport(invoiceDetailPdfParams as ArCollectionQueueParams);
          }}
          disabled={isDataLoading || isExporting}
        />
      </ActionsMenu>
    </div>
  );
}
